import { useCallback } from "react";
import { useDraftMode } from "../contexts/DraftModeContext";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useInvalidateTimeOffHistory } from "./useTimeOffHistory";

/**
 * Asks the cached schedule weeks again, so their time off shows a change (with the cache on, where every screen
 * reads time off). Every week, as time off can run over several. Also marks the answers read over a nurse's whole
 * history stale (RDO days, long time off). For a publish, whose changes are saved before the weeks reload: it runs
 * inside the draft mode provider, which publishes the draft.
 */
export const useReloadScheduleTimeOff = (): (() => Promise<void>) => {
	const { refreshScheduleCache } = useHospitalData();
	const invalidateTimeOffHistory = useInvalidateTimeOffHistory();
	return useCallback(async () => {
		invalidateTimeOffHistory();
		await refreshScheduleCache({ allWeeks: true });
	}, [refreshScheduleCache, invalidateTimeOffHistory]);
};

/**
 * After a PTO edit or delete: `useReloadScheduleTimeOff`, but not during a draft. Its edits live in the cached weeks
 * and a reload would drop them, and ending the draft reloads the weeks anyway; the whole-history answers are still
 * marked stale.
 */
export const useRefreshScheduleTimeOff = (): (() => Promise<void>) => {
	const { isDraftMode } = useDraftMode();
	const reloadScheduleTimeOff = useReloadScheduleTimeOff();
	const invalidateTimeOffHistory = useInvalidateTimeOffHistory();
	return useCallback(async () => {
		if (isDraftMode) invalidateTimeOffHistory();
		else await reloadScheduleTimeOff();
	}, [isDraftMode, reloadScheduleTimeOff, invalidateTimeOffHistory]);
};
